import React from 'react';
import { Theme } from '@config/themes/ITheme';
import type { HistoryHandheldLayoutEntry } from '@core/gameengine/controller/BaseGameController';

// DeviceType is shared between games, import from crash for type compatibility
type DeviceType = 'desktop' | 'tablet' | 'mobile';

/**
 * Creates a style object for the HistoryView component.
 * @param theme - The theme object to apply to the styles.
 * @param deviceType - The current device type ('desktop', 'tablet', or 'mobile').
 * @returns A record of CSS properties for styling the component.
 */
export const historyStyles = (
  theme: Theme,
  deviceType: DeviceType,
  isLandscape: boolean,
  isIpad: boolean,
  isSkySniffer: boolean,
  usesCompactPurpleProfile: boolean = false,
  handheldLayout: HistoryHandheldLayoutEntry | null = null
): Record<string, React.CSSProperties> => {
  const skyxHandheldLayout =
    usesCompactPurpleProfile && handheldLayout ? handheldLayout : null;

  const baseWrapStyles: React.CSSProperties = {
    // position: 'relative' as const,
    // display: 'flex',
    // borderRadius: '7px',
    // width: '100%',
    // height: '100%',
  };

  return {
    historyChip: {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize:
        isLandscape || (isIpad && deviceType === 'desktop' && !isLandscape)
          ? '1.5vw'
          : '12px',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      flexShrink: 0,
      whiteSpace: 'nowrap',
      marginRight: '4px',
      userSelect: 'none',
      ...(usesCompactPurpleProfile && {
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
        padding: deviceType === 'mobile' ? '0 5px' : '0 6px',
        marginRight: deviceType === 'mobile' ? '8px' : '10px',
        fontSize: deviceType === 'mobile' ? '10px' : undefined,
        fontWeight: deviceType === 'mobile' ? '500' : '600',
      }),
    },

    payoutsWrapper: {
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      zIndex: 20,
      userSelect: 'none',
    },

    /** Right-side fade overlay to mask chips scrolling behind the toggle button. */
    fadeOverlay: {
      position: 'absolute' as const,
      right: 0,
      top: 0,
      width: usesCompactPurpleProfile
        ? (skyxHandheldLayout?.fadeWidth ??
          (deviceType === 'mobile' ? '56px' : '64px'))
        : '80px',
      height: '100%',
      pointerEvents: 'none',
      background: usesCompactPurpleProfile
        ? 'linear-gradient(to right, rgba(23, 5, 80, 0) 0%, rgba(23, 5, 80, 0.55) 30%, rgba(23, 5, 80, 0.88) 62%, rgba(23, 5, 80, 1) 100%)'
        : 'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(8,8,8,0.55) 25%, rgba(8,8,8,0.78) 55%, rgba(3,3,3,0.95) 85%, rgba(0,0,0,1) 100%)',
      boxShadow: usesCompactPurpleProfile
        ? 'none'
        : 'inset -40px 0 95px rgba(0, 0, 0, 2)',
      filter: usesCompactPurpleProfile ? 'none' : 'blur(2px)',
      zIndex: 26,
      opacity: 1,
      transition: 'opacity 0.3s ease, transform 0.4s ease',
      transform: usesCompactPurpleProfile ? 'none' : 'translateX(38px)',
    },

    historyWrap: {
      display: 'flex',
      alignItems: 'center',
      position: 'relative' as const,
      height: '40px',
      width: '100%',
      padding: '6px 4px',
      borderRadius: '16px',
      ...(usesCompactPurpleProfile
        ? {
            height:
              skyxHandheldLayout?.wrapHeight ??
              (deviceType === 'mobile' ? '42px' : '34px'),
            padding:
              skyxHandheldLayout?.wrapPadding ??
              (deviceType === 'mobile' ? '0 8px' : '0 8px'),
            borderRadius:
              skyxHandheldLayout?.wrapBorderRadius ??
              (deviceType === 'mobile' ? '10px' : '16px 16px 0 0'),
            background: '#1F0A4D',
            border: '1px solid rgba(123,60,255,0.3)',
            ...(skyxHandheldLayout ? {} : { borderBottom: 'none' }),
            paddingRight: '36px',
          }
        : {}),
      userSelect: 'none',
      transform: 'translateZ(0)', // improves GPU sync
      willChange: 'transform',
    },

    /** sky-x new: open/close control stays fixed on the right while expanded. */
    historyToggleFixedSlot: usesCompactPurpleProfile
      ? {
          position: 'absolute' as const,
          right:
            skyxHandheldLayout?.toggleRight ??
            (deviceType === 'mobile' ? '11px' : '11px'),
          top:
            skyxHandheldLayout?.toggleTop ??
            (deviceType === 'mobile' ? '21px' : '17px'),
          transform: 'translateY(-50%)',
          zIndex: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto' as const,
        }
      : {},

    toggleButtonContainer: {
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      userSelect: 'none',
      zIndex: 25,
    },

    historyToggleButton: {
      position: 'relative' as const,
      height: '16px',
      padding: '0 4px',
      borderRadius: '32px',
      background: '#2c2d30',
      border: 'none',
      color: '#a0a3af',
      zIndex: 25,
      cursor: 'pointer',

      ...(isSkySniffer && {
        boxShadow: 'none',
      }),
      ...(usesCompactPurpleProfile && {
        background: '#0e0430',
        border: '1px solid #a13ef8',
        color: '#ffffff',
        width: deviceType === 'mobile' ? '22px' : '23px',
        height: deviceType === 'mobile' ? '18px' : '16px',
      }),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none',
      transform: 'none',
      width: '23px',
      marginRight: '7px',
      marginTop: '1px',
    },

    ellipsis: {
      fontSize: '13px',
      fontWeight: 700,
      lineHeight: 1,
      transform: 'translateY(-3.5px)',
      userSelect: 'none',
    },

    historyExpandedOverlay: {
      ...baseWrapStyles,
      display: 'flex',
      flexDirection: 'column',
      padding: '8px',
      background: usesCompactPurpleProfile
        ? 'rgba(26, 8, 86, 0.96)'
        : '#1b1c1d',
      border: usesCompactPurpleProfile
        ? '1px solid rgba(90, 43, 200, 0.7)'
        : 'none',
      minHeight: '80px',
      backdropFilter: 'blur(10px)',
      position: 'relative' as const,
      zIndex: 2000,
      pointerEvents: 'auto',
      margin:
        usesCompactPurpleProfile && skyxHandheldLayout?.expandedMargin != null
          ? skyxHandheldLayout.expandedMargin
          : '4px 0',
      borderRadius: usesCompactPurpleProfile ? '10px' : '16px',
      userSelect: 'none',
      top:
        usesCompactPurpleProfile && skyxHandheldLayout
          ? skyxHandheldLayout.expandedTop
          : '-3px',
      ...(usesCompactPurpleProfile
        ? {
            paddingRight: '36px',
            height: 'auto',
            minHeight: skyxHandheldLayout?.wrapHeight ?? '42px',
          }
        : {}),
    },

    historyHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '4px',
      width: '100%',
      flexShrink: 0,
      userSelect: 'none',
      ...(usesCompactPurpleProfile
        ? {
            justifyContent: 'flex-start',
            paddingRight: '4px',
          }
        : {}),
    },

    historyTitle: {
      fontSize: '12px',
      fontWeight: '500',
      color: usesCompactPurpleProfile ? '#d2c2ff' : '#a3a3a3',
      userSelect: 'none',
    },

    historyToggleCloseButton: {
      background: usesCompactPurpleProfile
        ? 'rgba(44, 17, 120, 0.9)'
        : 'rgba(50, 50, 50, 0.8)',
      borderRadius: '32px',
      border: usesCompactPurpleProfile
        ? '1px solid rgba(143, 100, 255, 0.8)'
        : 'none',
      color: usesCompactPurpleProfile ? '#f1eaff' : '#a0a3af',
      width: '23px',
      height: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      cursor: 'pointer',
      lineHeight: 1,
      padding: '0 4px',
      transition: 'none',
      userSelect: 'none',
      boxShadow: 'none',
      transform: 'none',
      marginTop: '-2px',
      marginRight: '3px',
    },

    historyContent: {
      display: 'flex',
      flexWrap: 'wrap',
      rowGap: '4px',
      columnGap: '2px',
      overflowY: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
    },
  };
};
